import { create } from 'zustand'

import {
  comparisonManager,
  comparisonService,
  DEFAULT_COMPARISON_FILTERS,
  DEFAULT_COMPARISON_PREFERENCES,
} from '@/engines/comparison'
import type {
  ComparisonBuildResult,
  ComparisonFiltersState,
  ComparisonPreferences,
  ComparisonPriceHistoryEntry,
  ComparisonRow,
  ComparisonSortMode,
} from '@/engines/comparison'

interface UnifiedComparisonState {
  ready: boolean
  result: ComparisonBuildResult
  filters: ComparisonFiltersState
  preferences: ComparisonPreferences
  selected: string[]
  activeRow: ComparisonRow | null
  history: ComparisonPriceHistoryEntry[]

  hydrate: () => void
  loadCatalog: (term?: string) => void
  loadDemo: () => void
  setQuery: (query: string) => void
  patchFilters: (patch: Partial<ComparisonFiltersState>) => void
  setSortMode: (mode: ComparisonSortMode) => void
  toggleSelect: (fingerprint: string) => void
  selectRow: (row: ComparisonRow) => void
  clearSelection: () => void
  refreshHistory: (fingerprint?: string) => void
}

const emptyResult: ComparisonBuildResult = {
  rows: [],
  summary: {
    rowCount: 0,
    offerCount: 0,
    bestPrice: null,
    totalEconomy: 0,
    recommendedSupplier: null,
    selectedCount: 0,
    selectedTotal: 0,
    estimatedMargin: null,
  },
  suppliers: [],
}

function syncFromManager(
  set: (partial: Partial<UnifiedComparisonState>) => void,
  result: ComparisonBuildResult,
) {
  set({
    ready: true,
    result,
    filters: comparisonManager.getFilters(),
    preferences: comparisonManager.getPreferences(),
    selected: comparisonManager.getSelectedFingerprints(),
    history: comparisonManager.listPriceHistory(),
  })
}

export const useUnifiedComparisonStore = create<UnifiedComparisonState>((set, get) => ({
  ready: false,
  result: emptyResult,
  filters: { ...DEFAULT_COMPARISON_FILTERS },
  preferences: { ...DEFAULT_COMPARISON_PREFERENCES },
  selected: [],
  activeRow: null,
  history: [],

  hydrate: () => {
    const preferences = comparisonService.getPreferences()
    comparisonManager.setPreferences(preferences)
    set({
      ready: true,
      preferences,
      history: comparisonManager.listPriceHistory(),
    })
  },

  loadCatalog: (term) => {
    const result = comparisonService.loadFromSearchCatalog(term)
    syncFromManager(set, result)
  },

  loadDemo: () => {
    const result = comparisonService.loadDemoCategories()
    syncFromManager(set, result)
  },

  setQuery: (query) => {
    const result = comparisonManager.setFilters({ query })
    syncFromManager(set, result)
  },

  patchFilters: (patch) => {
    const result = comparisonManager.setFilters(patch)
    syncFromManager(set, result)
  },

  setSortMode: (sortMode) => {
    const result = comparisonService.setPreferences({ sortMode })
    syncFromManager(set, result)
  },

  toggleSelect: (fingerprint) => {
    const result = comparisonManager.toggleSelect(fingerprint)
    syncFromManager(set, result)
  },

  selectRow: (row) => {
    set({ activeRow: row })
    get().refreshHistory(row.fingerprint)
  },

  clearSelection: () => {
    const result = comparisonManager.clearSelection()
    syncFromManager(set, result)
  },

  refreshHistory: (fingerprint) => {
    set({ history: comparisonManager.listPriceHistory(fingerprint) })
  },
}))
